import React, { useEffect, useState } from 'react';
import { Building, Search, PlusCircle, Filter, Loader2, ChevronDown, Trash2, Edit, Globe } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../../../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../../components/ui/dropdown-menu';
import { api } from '../../../lib/api';
import { toast } from 'sonner';
import { Badge } from '../../../components/ui/badge';
import LoeschenMitGrundDialog, { grundHeader } from '../../../components/ui/loeschen-mit-grund-dialog';

interface Standort {
  id: number;
  name: string;
  slug: string;
  adresse?: string;
  telefon?: string;
  email?: string;
  website?: string;
  aktiv: boolean;
}

const TenantManagement = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [items, setItems] = useState<Standort[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [zuLoeschen, setZuLoeschen] = useState<Standort | null>(null);

  const load = async () => {
    setIsLoading(true);
    try {
      const data = await api.get<Standort[]>('/api/standorte', {
        params: { active_only: 'false' },
      });
      setItems(data);
    } catch (e) {
      console.error(e);
      toast.error('Standorte konnten nicht geladen werden');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = items.filter(s => {
    const matchesSearch = searchTerm === '' || (s.name?.toLowerCase().includes(searchTerm.toLowerCase())) || (s.adresse?.toLowerCase().includes(searchTerm.toLowerCase())) || (s.email?.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = statusFilter === 'all' || (statusFilter === 'active' && s.aktiv) || (statusFilter === 'inactive' && !s.aktiv);
    return matchesSearch && matchesStatus;
  });

  const getStatusText = (aktiv: boolean) => (aktiv ? 'Aktiv' : 'Inaktiv');
  const hasNoResults = !isLoading && filtered.length === 0;
  const isListVisible = !isLoading && !hasNoResults;

  const handleDelete = async (s: Standort, grund: string) => {
    await api.delete(`/api/standorte/${s.id}`, grundHeader(grund));
    setItems(prev => prev.filter(i => i.id !== s.id));
    setZuLoeschen(null);
    toast.success('Standort gelöscht');
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold leading-7">Standortverwaltung</h1>
        <p className="text-muted-foreground">Verwalten Sie Standorte (CRUD, persistiert in der Datenbank)</p>
      </div>

      <div className="flex flex-col space-y-4 md:flex-row md:items-center md:justify-between md:space-y-0">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input uiSize="md" type="search" placeholder="Suchen..." className="pl-8 md:w-[250px] lg:w-[300px]" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger size="md" className="w-[150px]">
              <div className="flex items-center">
                <Filter className="mr-2 h-4 w-4" />
                <span>Status</span>
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle</SelectItem>
              <SelectItem value="active">Aktiv</SelectItem>
              <SelectItem value="inactive">Inaktiv</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => navigate('/system/organization/tenants/new')}>
          <PlusCircle className="mr-2 h-4 w-4" />
          Neuer Standort
        </Button>
      </div>

      {isLoading && (
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}
      {hasNoResults && (
        <div className="rounded-lg border bg-card p-8 text-center">
          <Building className="mx-auto h-12 w-12 text-muted-foreground" />
          <h2 className="mt-4 text-lg font-semibold">Keine Standorte gefunden</h2>
          <p className="mt-2 text-sm text-muted-foreground">Passen Sie die Suche an oder fügen Sie einen neuen Standort hinzu.</p>
          <Button className="mt-4" onClick={() => navigate('/system/organization/tenants/new')}>
            <PlusCircle className="mr-2 h-4 w-4" />
            Neuen Standort hinzufügen
          </Button>
        </div>
      )}
      {isListVisible && (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map(s => (
            <Card key={s.id} className="overflow-hidden">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Globe className="h-5 w-5 text-indigo-500" />
                    <span className="ml-2 text-sm text-muted-foreground">{s.slug}</span>
                  </div>
                  <Badge size="sm" variant={s.aktiv ? 'success' : 'secondary'}>{getStatusText(s.aktiv)}</Badge>
                </div>
                <CardTitle className="mt-2 line-clamp-1">{s.name}</CardTitle>
                <CardDescription className="line-clamp-2">{s.adresse || 'Keine Adresse'}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  {s.email && <div className="flex justify-between"><span className="text-muted-foreground">E‑Mail:</span><span className="truncate ml-2">{s.email}</span></div>}
                  {s.telefon && <div className="flex justify-between"><span className="text-muted-foreground">Telefon:</span><span className="ml-2">{s.telefon}</span></div>}
                  {s.website && <div className="flex justify-between"><span className="text-muted-foreground">Website:</span><span className="truncate ml-2">{s.website}</span></div>}
                </div>
              </CardContent>
              <CardFooter className="border-t bg-muted/50 px-6 py-3">
                <div className="flex justify-between w-full">
                  <Button variant="outline" size="sm" onClick={() => navigate(`/system/organization/tenants/${s.id}`)}>Details</Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <span className="sr-only">Menü öffnen</span>
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/system/organization/tenants/${s.id}`)}>
                        <Edit className="mr-2 h-4 w-4" />
                        <span>Bearbeiten</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => setZuLoeschen(s)}>
                        <Trash2 className="mr-2 h-4 w-4" />
                        <span>Löschen</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardFooter>
            </Card>
          ))}
        </div>
      )}

      <LoeschenMitGrundDialog
        offen={zuLoeschen !== null}
        titel={`Standort „${zuLoeschen?.name ?? ''}“ löschen?`}
        onAbbrechen={() => setZuLoeschen(null)}
        onLoeschen={async (grund) => {
          if (zuLoeschen) await handleDelete(zuLoeschen, grund);
        }}
      />
    </div>
  );
};

export default TenantManagement;